import {Popover, Typography} from "@mui/material";
import {Canvas, useThree} from "@react-three/fiber";
import {OrthographicCamera} from "@react-three/drei";
import DetailCanvas from "../../../../pages/customer/processing/DetailProcessing/Canvas/DetailCanvas";
import React, {useEffect, useMemo, useState} from "react";
import {
    getPreviewBoxSize,
    INIT_SCALE_PREV_DETAIL,
    PREVIEW_FIT_PADDING,
    PREVIEW_MAX_SIZE,
    PREVIEW_OFFSET,
    PREVIEW_VIEWPORT_PADDING,
    shouldOpenPreviewOnLeft
} from "../../../../utils/processing/calcNewScale";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {useErrorBoundary} from "use-error-boundary";
import {getModelDetailCached} from "../../../../utils/cutting/modelDetailCache";

const PreviewFitCamera = ({worldWidth, worldHeight}) => {
    const size = useThree((s) => s.size);
    const sizeW = Math.max(size.width, 1);
    const sizeH = Math.max(size.height, 1);
    const zoom = Math.min(
        sizeW / Math.max(worldWidth, 0.001),
        sizeH / Math.max(worldHeight, 0.001),
    ) * PREVIEW_FIT_PADDING;

    return (
        <OrthographicCamera
            makeDefault
            position={[0, 0, 100]}
            zoom={Math.max(zoom, 0.001)}
            near={0.1}
            far={1000}
        />
    );
};

const SeeDetail = ({detailData, params, boxWidth, boxHeight}) => {
    const {ErrorBoundary, didCatch} = useErrorBoundary()
    const width = boxWidth || PREVIEW_MAX_SIZE
    const height = boxHeight || PREVIEW_MAX_SIZE
    const worldWidth = Math.max(Number(detailData?.length) || 0, 0.001) * params.scale
    const worldHeight = Math.max(Number(detailData?.width) || 0, 0.001) * params.scale

    return <Box
        sx={{
            width,
            height,
        }}>
        {didCatch ? (
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                width,
                minHeight: "1px !important",
                padding: "10px"
            }}>
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: (theme) => theme.palette.error.light
                    }}>Ваших системных ресурсов не достаточно для отрисовки детали
                </Typography>
            </Box>
        ) : (
            <ErrorBoundary>
                <Canvas>
                    <PreviewFitCamera worldWidth={worldWidth} worldHeight={worldHeight}/>
                    <DetailCanvas
                        reaOnly={true}
                        position={params.corePosition}
                        detailData={detailData}
                        scale={params.scale}
                    />
                </Canvas>
            </ErrorBoundary>)}
    </Box>
}

const Loading = React.memo(({boxWidth, boxHeight}) => (
    <Box sx={{
        width: boxWidth,
        height: boxHeight,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
    }}>
        <CircularProgress disableShrink/>
    </Box>
))

const ModalSeeDetail = ({open, seeDetailData, handleClose, loadModelDetail}) => {
    const [params, setParams] = useState({scale: INIT_SCALE_PREV_DETAIL, corePosition: [0, 0, 0]});
    const [isLoading, setIsLoading] = useState(false);
    const [fullDetail, setFullDetail] = useState(null);
    const [isFetching, setIsFetching] = useState(false);

    const detailId = seeDetailData.id;
    const detailData = loadModelDetail ? fullDetail : seeDetailData.data;

    const previewSize = useMemo(() => {
        const source = detailData || seeDetailData.data;
        return getPreviewBoxSize(source?.length, source?.width);
    }, [detailData, seeDetailData.data]);

    const openOnLeft = useMemo(() => {
        if (!open || !seeDetailData.position) {
            return false;
        }
        const source = seeDetailData.data;
        return shouldOpenPreviewOnLeft(seeDetailData.position, source?.length, source?.width);
    }, [open, seeDetailData.position, seeDetailData.data]);

    useEffect(() => {
        setFullDetail(null);
    }, [detailId]);

    useEffect(() => {
        if (!loadModelDetail || !open || detailId == null) {
            return;
        }

        let cancelled = false;
        setIsFetching(true);

        getModelDetailCached(detailId)
            .then((data) => {
                if (!cancelled) {
                    setFullDetail(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFullDetail(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsFetching(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, detailId, loadModelDetail]);

    useEffect(() => {
        if (!open || !detailData?.detailContour?.points?.length) {
            return;
        }

        const newScale = INIT_SCALE_PREV_DETAIL;
        const startX = -(detailData.length / 2 * newScale);
        const startY = -(detailData.width / 2 * newScale);
        setParams({scale: newScale, corePosition: [startX, startY, 0]});
        setIsLoading(false);
    }, [detailData, open]);

    const showLoading = (loadModelDetail && isFetching)
        || !detailData?.detailContour?.points?.length
        || isLoading;

    const popperOffset = openOnLeft ? -PREVIEW_OFFSET : PREVIEW_OFFSET;

    return <Popover
        id="mouse-over-popover"
        anchorEl={seeDetailData.position}
        anchorOrigin={{
            vertical: "center",
            horizontal: openOnLeft ? "left" : "right"
        }}
        transformOrigin={{
            vertical: 'center',
            horizontal: openOnLeft ? 'right' : 'left',
        }}
        disableScrollLock
        keepMounted
        slotProps={{
            paper: {
                sx: {
                    pointerEvents: 'none',
                    width: previewSize.boxWidth,
                    minWidth: previewSize.boxWidth,
                    height: previewSize.boxHeight,
                    minHeight: previewSize.boxHeight,
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                },
            },
            popper: {
                strategy: 'fixed',
                placement: openOnLeft ? 'left' : 'right',
                modifiers: [
                    {name: 'offset', options: {offset: [popperOffset, 0]}},
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: PREVIEW_VIEWPORT_PADDING,
                            boundary: 'viewport',
                            altBoundary: true,
                        },
                    },
                    {name: 'flip', enabled: false},
                    {
                        name: 'computeStyles',
                        options: {adaptive: false},
                    },
                ],
            },
        }}
        sx={{
            pointerEvents: 'none',
        }}
        open={open}
        onClose={handleClose}
        disableRestoreFocus>
        {showLoading
            ? <Loading boxWidth={previewSize.boxWidth} boxHeight={previewSize.boxHeight}/>
            : (
                <SeeDetail
                    key={`${detailId ?? seeDetailData.data?.id}-${previewSize.boxWidth}x${previewSize.boxHeight}`}
                    detailData={detailData}
                    params={params}
                    boxWidth={previewSize.boxWidth}
                    boxHeight={previewSize.boxHeight}
                />)}
    </Popover>
}

export default ModalSeeDetail
